import { clsx } from "clsx";

import { Container } from "~/components/container";
import { FadeIn, FadeInStagger } from "~/components/fade-in";

function Module({
  name,
  platform,
  children,
  callout,
  shared = false,
}: {
  name: string;
  platform: string;
  children: React.ReactNode;
  callout: string;
  shared?: boolean;
}) {
  return (
    <div
      className={clsx(
        "relative rounded-xl p-5 sm:p-6",
        shared
          ? "border-2 border-accent-400 bg-accent-400/[0.07]"
          : "border border-ink-600 bg-ink-900",
      )}
    >
      <span
        aria-hidden="true"
        className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-accent-400 font-mono text-xs font-bold text-ink-950"
      >
        {callout}
      </span>
      <p className="font-mono text-sm font-medium text-ink-50">
        {name}
        <span className="text-ink-400"> · {platform}</span>
      </p>
      <p className="mt-3 text-sm text-ink-300">{children}</p>
    </div>
  );
}

const notes = [
  "Users never notice the shared layer — that is the point. Both apps keep their own idioms, their own gestures, their own release cadence.",
  "Adoption is incremental. We start with one feature, prove it in production, then widen. No rewrite, no big-bang release.",
  "A logic bug is fixed in one place. Features stop landing on Android in March and iOS in June.",
];

/**
 * Fig. 1 — the boundary between shared and platform code, drawn rather than
 * described. Carving that line is the work, so it gets the page's one diagram.
 */
export function ModuleDiagram({ className }: { className?: string }) {
  return (
    <Container className={className}>
      <FadeIn>
        <p className="label text-accent-400">Kotlin Multiplatform</p>
        <h2 className="mt-5 max-w-2xl font-display text-4xl font-medium tracking-tight text-ink-50 [text-wrap:balance] sm:text-5xl">
          We draw the line between shared and native — then build both sides.
        </h2>
      </FadeIn>

      <FadeIn className="mt-12 rounded-2xl border border-ink-700 bg-ink-900/60 p-5 sm:p-8">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-dashed border-ink-600 pb-4">
          <p className="label text-ink-400">Fig. 1 — Module boundary</p>
          <p className="label text-ink-400">
            Targets: iosArm64 · androidTarget
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-6">
          <Module name="iosApp/" platform="SwiftUI" callout="1">
            Screens, navigation, gestures, haptics. Written the way iOS expects.
          </Module>
          <Module name="androidApp/" platform="Compose" callout="2">
            Screens, navigation, predictive back, widgets. Written the way
            Android expects.
          </Module>
        </div>

        <svg
          viewBox="0 0 400 44"
          preserveAspectRatio="none"
          fill="none"
          aria-hidden="true"
          className="h-8 w-full text-ink-500 sm:h-11"
        >
          <path
            d="M100 0 L100 26 L200 26 L200 40"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="4 3"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d="M300 0 L300 26 L200 26"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="4 3"
            vectorEffect="non-scaling-stroke"
          />
          <path d="M196 34 L200 44 L204 34 Z" fill="currentColor" />
        </svg>

        <Module
          shared
          name="shared/"
          platform="Kotlin Multiplatform"
          callout="3"
        >
          Networking · persistence · auth · analytics · business rules · state
          machines. One implementation, compiled for both targets — written
          once, tested once, fixed once.
        </Module>

        <div className="mt-6 flex items-center gap-4">
          <span className="label whitespace-nowrap text-ink-400">
            One implementation
          </span>
          <span className="relative h-px flex-auto bg-ink-600 before:absolute before:left-0 before:top-[-3px] before:h-[7px] before:w-px before:bg-ink-600 after:absolute after:right-0 after:top-[-3px] after:h-[7px] after:w-px after:bg-ink-600" />
          <span className="label whitespace-nowrap text-ink-400">
            Two targets
          </span>
        </div>
      </FadeIn>

      <FadeInStagger className="mt-10">
        <ul className="grid gap-5 sm:grid-cols-3">
          {notes.map((note, index) => (
            <li key={note}>
              <FadeIn className="flex gap-3">
                <span
                  aria-hidden="true"
                  className="mt-0.5 flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-accent-400 font-mono text-[10px] font-bold text-ink-950"
                >
                  {index + 1}
                </span>
                <p className="text-sm text-ink-300">{note}</p>
              </FadeIn>
            </li>
          ))}
        </ul>
      </FadeInStagger>
    </Container>
  );
}
